import React from 'react';
import PropTypes from 'prop-types';

import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { truncate } from 'lodash';

import {
  desaturatedBlue,
  ghostWhite,
  lightIndigo,
  shadowDark,
  shadowLight,
  shadowVeryLight,
  slateGrey,
  warmBlue,
  white,
  softBlue,
  warmGray,
} from 'lib/css/colors';
import { DISPLAY_NAME_LENGTH } from 'components/codify_db/constants';
import { formatGMV } from 'lib/utils/FormatUtils';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import { titleCase } from 'lib/utils/StringProcess';

function ManufacturerCard({
  manufacturer,
  onManufacturerClick,
  onDistributorClick,
  onOperatorClick,
}) {
  const manufacturerImageUrl = manufacturer.logo_url ?? placeholderProductImage;
  const hasGmvData =
    manufacturer.annualized_monitizable_gmv && manufacturer.annualized_monitizable_gmv > 0;

  const handleClick = () => {
    onManufacturerClick(manufacturer);
  };

  const handleDistributorClick = e => {
    e.stopPropagation();
    if (onDistributorClick && manufacturer.distributor_count > 0) {
      onDistributorClick(manufacturer);
    }
  };

  const handleOperatorClick = e => {
    e.stopPropagation();
    if (onOperatorClick && manufacturer.operator_count > 0) {
      onOperatorClick(manufacturer);
    }
  };

  return (
    <Card
      className={classNames('position-relative', css(styles.card), pointerOnHover)}
      onClick={handleClick}
    >
      <div className={classNames(css(styles.cardContent), 'd-flex flex-column h-100')}>
        <div
          className={classNames(
            css(styles.manufacturerImageContainer),
            'align-items-center justify-content-center d-flex',
          )}
        >
          <Card.Img
            className={css(styles.manufacturerImage)}
            variant="top"
            src={manufacturerImageUrl}
            alt={manufacturer.name}
          />
        </div>
        <div className={css(styles.cardBody)}>
          <div className={css(styles.displayName)}>
            {titleCase(truncate(manufacturer.name, { length: DISPLAY_NAME_LENGTH, omission: '' }))}
            {manufacturer.name.length > DISPLAY_NAME_LENGTH && (
              <OverlayTrigger overlay={<Tooltip>{titleCase(manufacturer.name)}</Tooltip>}>
                <span>...</span>
              </OverlayTrigger>
            )}
          </div>
          <div className={css(styles.subText)}>
            <div className={css(styles.infoLine)}>
              <span
                className={classNames(
                  css(styles.gmvValueOnly),
                  hasGmvData ? css(styles.gmvValue) : css(styles.noDataValue),
                )}
              >
                {formatGMV(manufacturer.annualized_monitizable_gmv)}
              </span>
            </div>
            <div
              className={classNames(
                css(styles.infoLine),
                manufacturer.distributor_count > 0 && css(styles.clickableLine),
              )}
              onClick={handleDistributorClick}
            >
              <span className={css(styles.infoLabel)}>Distributors:</span>
              <span className={css(styles.distributorValue)}>
                {manufacturer.distributor_count || 0}
              </span>
            </div>
            <div
              className={classNames(
                css(styles.infoLine),
                manufacturer.operator_count > 0 && css(styles.clickableLine),
              )}
              onClick={handleOperatorClick}
            >
              <span className={css(styles.infoLabel)}>Operators:</span>
              <span className={css(styles.operatorValue)}>{`(${
                manufacturer.tam_operator_count || 0
              })`}</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    width: 220,
    height: 220,
    borderRadius: 12,
    margin: '10px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    border: '1px solid ' + lightIndigo,
    backgroundColor: white,
    boxShadow: '0 2px 8px ' + shadowLight,
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px ' + shadowDark,
      border: '1px solid ' + warmBlue,
    },
  },
  cardContent: {
    height: '100%',
    padding: '10px',
  },
  manufacturerImageContainer: {
    position: 'relative',
    width: '100%',
    height: '125px',
    overflow: 'hidden',
    borderRadius: '8px',
    backgroundColor: ghostWhite,
    marginBottom: '6px',
  },
  manufacturerImage: {
    maxWidth: '98%',
    maxHeight: '98%',
    objectFit: 'contain',
    transition: 'transform 0.3s ease',
    ':hover': {
      transform: 'scale(1.05)',
    },
  },
  cardBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  displayName: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '1.3',
    color: desaturatedBlue,
    marginBottom: '6px',
    textAlign: 'center',
    minHeight: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    width: '100%',
    padding: '6px 0',
  },

  infoLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 10px',
    borderRadius: '6px',
    backgroundColor: ghostWhite,
    transition: 'all 0.2s ease',
  },

  clickableLine: {
    cursor: 'pointer',
    backgroundColor: lightIndigo,
    ':hover': {
      backgroundColor: lightIndigo,
      transform: 'translateX(2px)',
      boxShadow: `0 2px 4px ${shadowVeryLight}`,
    },
  },

  infoLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: warmGray,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  gmvValueOnly: {
    fontSize: '15px',
    fontWeight: '700',
    letterSpacing: '-0.3px',
    width: '100%',
    textAlign: 'center',
  },

  infoValue: {
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '-0.3px',
  },

  gmvValue: {
    color: softBlue,
  },

  noDataValue: {
    color: slateGrey,
  },

  distributorValue: {
    fontSize: '12px',
    fontWeight: '700',
    color: softBlue,
  },

  operatorValue: {
    fontSize: '12px',
    fontWeight: '700',
    color: softBlue,
  },
});

ManufacturerCard.propTypes = {
  manufacturer: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    logo_url: PropTypes.string,
    annualized_monitizable_gmv: PropTypes.number,
    distributor_count: PropTypes.number,
    operator_count: PropTypes.number,
    tam_operator_count: PropTypes.number,
  }).isRequired,
  onManufacturerClick: PropTypes.func.isRequired,
  onDistributorClick: PropTypes.func,
  onOperatorClick: PropTypes.func,
};

export default ManufacturerCard;
